import { puedeRecibir } from '../../utils/politicaConsentimiento';

/**
 * ordenPlantillas — en qué orden se ofrecen las plantillas del banner de conversación
 * expirada.
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * POR QUÉ ES UNA FUNCIÓN APARTE
 * ───────────────────────────────────────────────────────────────────────────────
 * Estaba inline en `ChatDetalle.js`, dentro de un `setTemplates(...)` de una línea, y
 * ahí no se podía probar sin montar el componente entero con sus contextos y su
 * Firestore. Es el mismo motivo por el que se extrajo `CuerpoMensaje`.
 *
 * Y lo que decide es lo ÚNICO de este commit que el operador ve.
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * EL ORDEN LO DECIDE PARA QUÉ SE ABRIÓ EL MODAL, NO META
 * ───────────────────────────────────────────────────────────────────────────────
 * Antes se pintaban tal cual las devuelve la API, y eso ponía las promociones
 * primero: `pux_promo_prueba_2`, `pux_promo_prueba`, `pux_prueba_4b`, y sólo después
 * las cuatro variantes del recordatorio de cita. Quien abre este modal viene del
 * banner «esta conversación expiró» — viene a retomar una conversación, y lo primero
 * que veía era una promoción.
 *
 * Eso no era una decisión: era el orden de Meta. Ahora sí lo es.
 *
 * ⚠️ LAS MARKETING SIGUEN VISIBLES, al final. Esconderlas sin bloquearlas daría una
 * falsa sensación de que algo las controla, y hoy no hay nada: el envío de plantilla
 * desde la bandeja NO comprueba consentimiento —medido el 21-sep-2026, sólo
 * `integrations/campanas/politicaConsentimiento.js` aplica la política—, así que una
 * MARKETING enviada desde aquí llega a quien escribió BAJA. Bloquearlas es el commit
 * de la puerta de consentimiento, no éste.
 */

/**
 * ¿Puede enviarse esta plantilla a este chat?
 *
 * Deriva con `puedeRecibir` —copia byte-idéntica de la del backend, con paridad— del
 * consentimiento que el chat YA trae por `onSnapshot`. Cero lecturas nuevas.
 *
 * ⚠️ NO ES LA AUTORIDAD. El backend vuelve a decidir antes del POST y devuelve 403:
 * este estado puede quedarse rancio si el chat lleva horas abierto, y una copia del
 * cliente no puede ser lo que protege un dato legal. Es un aviso, no una puerta —
 * la misma forma que la guarda de ventana.
 *
 * DEVUELVE EL MOTIVO, no sólo un booleano: el operador tiene que entender POR QUÉ no
 * puede enviarla. Una plantilla que desaparece sin explicación se lee como un fallo.
 */
export function veredictoDePlantilla(tpl, chatInfo) {
  // Sin categoría no se decide aquí: el servidor la resolverá —consultando a Meta si
  // hace falta— y ése es el que manda. Deshabilitarla en la UI por un dato que el
  // backend sí puede obtener sería un falso positivo.
  if (!tpl?.category) return { permitido: true, motivo: null };
  const contacto = {
    consentimiento: chatInfo?.consentimiento || null,
    primerMensajeEntrante: chatInfo?.primerMensajeEntrante || null,
  };
  return puedeRecibir(tpl.category, contacto);
}

/** La que el banner existe para ofrecer. Mismo literal que el módulo del backend. */
export const PLANTILLA_DE_REAPERTURA = 'retomar_conversacion';

/**
 * Grupo de una plantilla: 0 la de reapertura, 1 el resto de utilidad, 2 lo demás.
 *
 * Lo NO CLASIFICADO cae con MARKETING a propósito. Ausente = desconocido, y una
 * plantilla sin categoría podría ser MARKETING sin que haya forma de saberlo sin
 * preguntar a Meta: ponerla entre las de utilidad sería afirmar algo que no consta.
 */
export function grupoDePlantilla(tpl) {
  if (!tpl) return 2;
  if (tpl.name === PLANTILLA_DE_REAPERTURA) return 0;
  if (tpl.category === 'UTILITY') return 1;
  return 2;
}

/**
 * Ordena las plantillas ya filtradas a APPROVED.
 *
 * Alfabético dentro de cada grupo, y con el idioma como tercer criterio: así el orden
 * NO depende de lo que devuelva Meta en cada llamada. Sin ese desempate, dos variantes
 * de la misma plantilla podían intercambiarse entre dos aperturas del modal.
 *
 * NO MUTA la lista recibida: `sort` sobre el array de `result.templates` reordenaría
 * el objeto del llamante.
 *
 * @param {Array<{name?:string, language?:string, category?:string}>} plantillas
 */
export function ordenarPlantillasDelBanner(plantillas) {
  return [...(plantillas || [])].sort((a, b) => (
    grupoDePlantilla(a) - grupoDePlantilla(b)
    || String(a?.name ?? '').localeCompare(String(b?.name ?? ''))
    || String(a?.language ?? '').localeCompare(String(b?.language ?? ''))
  ));
}

/**
 * ¿QUÉ VARIABLE DE ESTA PLANTILLA ES EL NOMBRE DEL NEGOCIO?
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * POR PLANTILLA, NUNCA POR POSICIÓN (24-sep-2026)
 * ───────────────────────────────────────────────────────────────────────────────
 * La tentación es prerellenar `{{1}}`, porque en `retomar_conversacion` esa es la
 * variable del negocio. Sería un defecto silencioso: en `appointment_reminder`, `{{1}}`
 * es **el nombre del CLIENTE** —su ejemplo es literalmente `["Rodrigo", …]`— y el
 * prerelleno produciría «Hola pux_app, te recordamos tu próxima cita», tratando al
 * cliente por el nombre del negocio.
 *
 * Por eso esto es una lista explícita de nombre → índice, y **lo que no está en la lista
 * no se prerellena**. Una plantilla que el cliente cree por su cuenta, o las tres
 * `pux_*` de prueba, siguen con todos sus campos en blanco: el operador los escribe.
 *
 * ⚠️ NO SE DERIVA DE LOS MÓDULOS DE `functions/`. El frontend no puede importarlos, y una
 * copia byte-idéntica aquí sería una tercera cosa que mantener para un mapa de dos
 * entradas. Lo que sí hay es un test que fija **el caso peligroso**: que
 * `appointment_reminder` NO esté, y que lo desconocido no se prerellene.
 */
export const VARIABLE_DEL_NEGOCIO = {
  retomar_conversacion: 1,
  // `seguimiento_solicitud` entrará aquí el día que exista en Meta. Hasta entonces no
  // se declara: una entrada para una plantilla que nadie puede seleccionar es una
  // afirmación sobre algo que no existe.
};

/**
 * Los valores iniciales de las variables de una plantilla.
 *
 * Devuelve TODAS las variables del cuerpo con cadena vacía, salvo la del negocio cuando
 * la plantilla la declara y hay un `verified_name` que poner.
 *
 * EL VALOR ES EDITABLE: esto rellena un input, no lo bloquea. `verified_name` es el
 * nombre que Meta verificó, y no siempre es el nombre con el que el negocio quiere
 * presentarse — el de PUX es `pux_app`.
 *
 * @param {{name?:string, components?:Array}} tpl
 * @param {string|null} verifiedName
 * @returns {Object<string,string>} por índice de variable, como los espera el modal
 */
export function valoresInicialesDePlantilla(tpl, verifiedName = null) {
  const cuerpo = (tpl?.components || []).find((c) => c.type === 'BODY');
  const texto = cuerpo?.text || '';
  const vars = {};
  (texto.match(/\{\{(\d+)\}\}/g) || []).forEach((m) => {
    vars[m.replace(/[{}]/g, '')] = '';
  });

  const indice = VARIABLE_DEL_NEGOCIO[tpl?.name];
  const nombre = typeof verifiedName === 'string' ? verifiedName.trim() : '';
  // `indice in vars` y no un `if (indice)`: si la plantilla declara la variable 1 pero su
  // cuerpo no la tiene, escribirla añadiría un parámetro que Meta rechaza por sobrante.
  if (indice && nombre && String(indice) in vars) {
    vars[String(indice)] = nombre;
  }
  return vars;
}

/**
 * QUÉ SIGNIFICA CADA VARIABLE DE LAS PLANTILLAS QUE PUX DEFINE.
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * POR QUÉ HACE FALTA (24-sep-2026)
 * ───────────────────────────────────────────────────────────────────────────────
 * El modal etiqueta los campos como «Variable {{1}}» con placeholder «Valor para {{1}}».
 * Con una variable era tolerable. Con dos —`seguimiento_solicitud` añade el asunto— es
 * el sitio exacto donde un operador mete el asunto en el campo del negocio y manda
 * «Hola, somos cotización de cerraduras».
 *
 * `appointment_reminder` entra también, y es el caso que más lo pedía: sus CUATRO campos
 * se presentan hoy como «Variable {{1}}»…«Variable {{4}}» y el primero es el nombre del
 * CLIENTE.
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * ⚠️ ES UN MAPA DISTINTO DE `VARIABLE_DEL_NEGOCIO`, Y NO SE FUSIONAN
 * ───────────────────────────────────────────────────────────────────────────────
 * Parecen lo mismo —los dos van por nombre de plantilla y por índice— y responden a
 * preguntas distintas:
 *
 *   · `VARIABLE_DEL_NEGOCIO`      ¿qué campo se RELLENA solo con `verified_name`?
 *   · `ETIQUETAS_DE_VARIABLES`    ¿cómo se LLAMA cada campo para el operador?
 *
 * Fusionarlos metería `appointment_reminder` en el primero, y entonces su `{{1}}` —el
 * nombre del cliente— se rellenaría con el nombre del negocio. Es exactamente el defecto
 * que `VARIABLE_DEL_NEGOCIO` existe para evitar, reintroducido por «simplificar».
 *
 * Los VALORES son claves de i18n, no texto: el modal lo abren operadores en tres idiomas.
 * Se reutilizan entre plantillas —`businessName` sale en dos— porque el concepto es el
 * mismo; lo que no se reutiliza es el índice, que es de cada plantilla.
 */
export const ETIQUETAS_DE_VARIABLES = {
  retomar_conversacion: { 1: 'businessName' },
  seguimiento_solicitud: { 1: 'businessName', 2: 'requestSubject' },
  appointment_reminder: { 1: 'customerName', 2: 'appointmentDate', 3: 'appointmentTime', 4: 'timeUntil' },
};

/**
 * La clave i18n de una variable, o null si la plantilla no la declara.
 *
 * Null y no una cadena vacía: el llamante tiene que poder distinguir «no sé cómo se
 * llama» —y entonces cae a «Variable {{N}}», que es el comportamiento de hoy— de «se
 * llama así». Una cadena vacía pintaría una etiqueta en blanco.
 */
export function claveDeEtiquetaDeVariable(templateName, indice) {
  const porPlantilla = ETIQUETAS_DE_VARIABLES[templateName];
  if (!porPlantilla) return null;
  return porPlantilla[String(indice)] || null;
}
